import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  BookOpen,
  FileClock,
  Link2,
  Settings,
  Shield,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { useTheme } from '../../../components/common/Theme';
import './ai-governance.css';

const GOVERNANCE_NAV = [
  { label: 'Policies', to: '/portal/ai-governance/policies', icon: Shield, end: true },
  { label: 'Policy Groups', to: '/portal/ai-governance/groups', icon: Users },
  { label: 'Assignments', to: '/portal/ai-governance/assignments', icon: Link2 },
  { label: 'Overrides', to: '/portal/ai-governance/overrides', icon: ShieldAlert },
  { label: 'Policy Library', to: '/portal/ai-governance/library', icon: BookOpen },
  { label: 'Audit Logs', to: '/portal/ai-governance/audit-logs', icon: FileClock },
  { label: 'Settings', to: '/portal/ai-governance/settings', icon: Settings },
];

export default function AIGovernanceLayout() {
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium border-l-2 transition-colors ${
      isActive
        ? isLight
          ? 'bg-indigo-50 text-indigo-700 border-l-indigo-600'
          : 'bg-neutral-900 text-white border-l-indigo-500'
        : isLight
          ? 'text-slate-600 border-l-transparent hover:bg-slate-100 hover:text-slate-900'
          : 'text-neutral-400 border-l-transparent hover:bg-neutral-900/60 hover:text-neutral-200'
    }`;

  return (
    <div
      className={`ai-governance flex h-full min-h-[calc(100vh-5rem)] -m-3 ${
        isLight ? 'bg-slate-100' : 'bg-neutral-950'
      }`}
    >
      <aside
        className={`w-52 shrink-0 flex flex-col border-r ${
          isLight ? 'bg-white border-slate-200' : 'bg-neutral-950 border-neutral-800'
        }`}
      >
        <div className={`px-4 py-4 border-b ${isLight ? 'border-slate-200' : 'border-neutral-800'}`}>
          <div className="flex items-center gap-2">
            <Shield className={`w-4 h-4 shrink-0 ${isLight ? 'text-indigo-600' : 'text-indigo-400'}`} />
            <div className="min-w-0">
              <p className={`text-[13px] font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`}>
                AI Governance
              </p>
              <p className={`text-[11px] truncate ${isLight ? 'text-slate-500' : 'text-neutral-500'}`}>
                Policy & compliance control
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2">
          <p
            className={`px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider ${
              isLight ? 'text-slate-400' : 'text-neutral-500'
            }`}
          >
            Navigation
          </p>
          <div className="space-y-0.5">
            {GOVERNANCE_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
                  <Icon className="w-3.5 h-3.5 shrink-0 opacity-80" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>

        <div className={`px-4 py-3 border-t ${isLight ? 'border-slate-200' : 'border-neutral-800'}`}>
          <p className={`text-[10px] leading-relaxed ${isLight ? 'text-slate-500' : 'text-neutral-600'}`}>
            Enterprise AI policy enforcement across LLMs, agents, and data assets.
          </p>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <div className="p-5 w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
